import { service } from "@maia/cart";
import { Button } from "@maia/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { flow } from "es-toolkit";
import { useCallback } from "react";
import { twMerge } from "tailwind-merge";
import { match } from "ts-pattern";
import { getPreconditionQuestionsOptions } from "../../../../features/edit-insurance/get-precondition-questions/queryOptions";
import { Form } from "../../../../features/form/base-form/base-form";
import { queryClient } from "../../../../main";
import { mapToAnswer } from "./../../../../features/form/utils/map-to-answers";

export const Route = createFileRoute(
  "/_authenticated/edit-insurance/$customer/cart/$cart/_layout/",
)({
  component: RouteComponent,
  loader: async ({
    context: { queryClient },
    params: { cart: cartGuid, customer: customerGuid },
  }) => {
    const preconditionQuestions = await queryClient.ensureQueryData(
      getPreconditionQuestionsOptions({ cartGuid, customerGuid }),
    );

    if (!preconditionQuestions.length) {
      throw redirect({
        to: "/edit-insurance/$customer/cart/$cart/compositions",
        params: { cart: cartGuid, customer: customerGuid },
      });
    }

    return preconditionQuestions;
  },
});

function RouteComponent() {
  const data = Route.useLoaderData();
  const params = Route.useParams();

  const navigate = Route.useNavigate();
  const { customer, cart } = Route.useParams();

  const {
    data: questions,
    refetch,
    isRefetching,
  } = useQuery({
    ...getPreconditionQuestionsOptions({
      cartGuid: cart,
      customerGuid: customer,
    }),
    initialData: data,
  });

  const { mutate, isPending } = useMutation({
    mutationFn: (body: {
      questions: Parameters<typeof service.putPreconditionMutationQuestions>[number]["questions"];
      trigger: "revalidate" | "submit";
    }) =>
      service.putPreconditionMutationQuestions({
        cartGuid: cart,
        customerGuid: customer,
        questions: body.questions,
      }),
    onSuccess: (_, request) =>
      match(request.trigger)
        .with("revalidate", () => refetch())
        .with("submit", async () => {
          await queryClient.invalidateQueries({
            queryKey: ["getMutationProducts", params.customer, params.cart],
          });
          await refetch();
          return navigate({
            to: "/edit-insurance/$customer/cart/$cart/compositions",
            params: { cart, customer },
          });
        })
        .exhaustive(),
  });

  const handleRevalidate = useCallback(
    flow(mapToAnswer, (questions) => mutate({ questions, trigger: "revalidate" })),
    [mutate],
  );

  const handleSubmit = useCallback(
    flow(mapToAnswer, (questions) => mutate({ questions, trigger: "submit" })),
    [mutate],
  );

  return (
    <Form
      className={twMerge(
        (isRefetching || isPending) &&
          "pointer-events-none opacity-25 transition-opacity duration-200",
        "flex flex-col gap-y-32",
      )}
      questions={questions}
      onSubmit={handleSubmit}
      onRevalidate={handleRevalidate}
      submitButtonSlot={
        <Button intent="primary" className="w-min ml-auto" type="submit">
          Volgende
        </Button>
      }
    />
  );
}
